import React from 'react';
import { graphql } from 'react-relay';
import { useLazyLoadQuery } from 'react-relay/hooks';
import classes from './AccountBalance.module.scss';
import TransactionList from '../../molecules/TransactionList/TransactionList';
import { AccountBalanceQuery } from './__generated__/AccountBalanceQuery.graphql';
import { FragmentedBalance } from '../../molecules/Balance/Balance';
import ErrorBoundary from '../../molecules/ErrorBoundary/ErrorBoundary';
import HistoryCharts from '../../molecules/HistoryCharts/HistoryCharts';
import OfferHistory from '../../molecules/OfferHistory/OfferHistory';
import useQueryStringParams from '../../../hooks/useQueryStringParams';
import { useWasShownReviewPrompt } from '../../organisms/ReviewPromptModal/ReviewPromptModal';
import useFrontendEventTracker from '../../../hooks/useFrontendEventTracker';
import { useModalContext, MODAL_TYPES } from '../../templates/ModalProvider/ModalProvider';
import Txt from '../../atoms/Txt/Txt';
import Notice from '../../atoms/Notice/Notice';
import CashoutProviderButtons from './CashoutProviderButtons';

export interface Props {}

const transactionListPagination = graphql`
  query AccountBalanceQuery {
    ...TransactionList_transactions
    offerHistory {
      ...OfferHistory_offerHistory
    }
    balance {
      ...Balance_balance
    }
    history {
      ...HistoryCharts_history
    }
    userData {
      currency {
        currencySymbol
      }
    }
    cashoutProviders {
      ...CashoutProviderButtons_providers
    }
  }
`;

const AccountBalance: React.FC<Props> = () => {
  const { showModal } = useModalContext();
  const startDate = new Date(1970, 0, 1, 0, 0, 0, 0).toISOString();
  const endDate = React.useMemo(() => new Date().toISOString(), []);

  const { successMessage, amount } = useQueryStringParams();
  const wasShownReviewPrompt = useWasShownReviewPrompt();
  const dayOfWeek = new Date().getDay();
  const isWeekend = dayOfWeek === 6 || dayOfWeek === 0;
  const [hasOpenPrompt, setHasOpenPrompt] = React.useState(false);

  const isShowReviewPrompt = React.useMemo(
    () =>
      !hasOpenPrompt &&
      !wasShownReviewPrompt &&
      !isWeekend &&
      successMessage === 'Successfully cashed out' &&
      amount &&
      parseInt(amount, 10) > 30,
    [hasOpenPrompt, wasShownReviewPrompt, successMessage, amount, isWeekend],
  );

  const trackFrontendEvent = useFrontendEventTracker();
  const handleCloseReviewDialog = React.useCallback(() => {
    trackFrontendEvent({
      type: 'review_dialog_closed',
      value: 'account_balance_page',
    });
  }, [trackFrontendEvent]);

  React.useEffect(() => {
    if (isShowReviewPrompt) {
      setHasOpenPrompt(true);
      const modalProps = {
        onClose: handleCloseReviewDialog,
      };
      showModal(MODAL_TYPES.REVIEW_PROMPT_MODAL, modalProps);
    }
    return undefined;
  }, [showModal, isShowReviewPrompt, handleCloseReviewDialog, setHasOpenPrompt]);

  const data = useLazyLoadQuery<AccountBalanceQuery>(
    transactionListPagination,
    { startDate, endDate },
    {
      fetchPolicy: 'store-or-network',
    },
  );
  const offerHistoryNotice = (
    <Notice
      message="We are having problems loading your offer history at the moment. Please try again later."
      status="warning"
    />
  );
  const historyNotice = (
    <Notice
      message="We are having problems loading your history at the moment. Please try again later."
      status="warning"
    />
  );
  const cashoutErrorNotice = (
    <Notice
      message="We are having problems loading cashouts at the moment. Please try again later."
      status="warning"
    />
  );
  const balanceErrorNotice = (
    <Notice
      message="We are having problems loading your balance at the moment. Please try again later."
      status="warning"
    />
  );
  const transactionsNotice = (
    <Notice
      message="We are having trouble loading transactions at the moment. Please try again later."
      status="warning"
    />
  );

  return (
    <div className={classes.root}>
      <ErrorBoundary fallback={balanceErrorNotice}>
        {data.balance ? (
          <h1 className={classes.balance}>
            <FragmentedBalance balanceRef={data.balance} />
          </h1>
        ) : (
          balanceErrorNotice
        )}
      </ErrorBoundary>
      <Txt component="h3" className={classes.cashoutsHeader}>
        Cash out to:
      </Txt>
      <ErrorBoundary fallback={cashoutErrorNotice}>
        {data.cashoutProviders ? (
          <CashoutProviderButtons cashoutProvidersRef={data.cashoutProviders} />
        ) : (
          cashoutErrorNotice
        )}
      </ErrorBoundary>
      <ErrorBoundary fallback={historyNotice}>
        <React.Suspense fallback={null}>
          {data.history ? (
            <HistoryCharts
              historyRef={data.history}
              currencySymbol={data.userData?.currency.currencySymbol || ''}
            />
          ) : (
            historyNotice
          )}
        </React.Suspense>
      </ErrorBoundary>
      <ErrorBoundary fallback={offerHistoryNotice}>
        <React.Suspense fallback={null}>
          {data.offerHistory ? (
            <OfferHistory offerHistoryRef={data.offerHistory} />
          ) : (
            offerHistoryNotice
          )}
        </React.Suspense>
      </ErrorBoundary>
      <ErrorBoundary fallback={transactionsNotice}>
        <React.Suspense fallback={null}>
          <div>{data ? <TransactionList transactionsRef={data} /> : transactionsNotice}</div>
        </React.Suspense>
      </ErrorBoundary>
    </div>
  );
};

export default AccountBalance;
